import {
  bulkMarkingInstruction,
  DotMarkingContainer,
  dotMarkingGroupFor,
  inhalationMarkingHeightMm,
  inhalationMarkingInstruction,
  packageLabelInstruction,
  requiresHazardLabel,
  requiresLargeIdNumber,
} from "./dotMarkingGroup";
import { printsRegulatedHazardLabel } from "./hazardClassDiamond";
import { poisonInhalationLine } from "./hazardousWasteLabelVisuals";

/** The label fields the DOT block's marking decisions read. */
export interface DotBlockMarkingContent {
  dotHazardClass?: string | null;
  dotSubsidiaryHazardClass?: string | null;
  dotIdNumber?: string | null;
}

export interface DotBlockMarking {
  showHazardLabels: boolean;
  inhalationLine: string | undefined;
  markingInstruction: string | undefined;
}

/**
 * The marking decisions the PDF and Oak's preview share. `showDotBlock` is
 * false when the stream has no DOT profile, which suppresses the hazard
 * labels. An undefined `container` is an unknown container type: no hazard
 * labels, no inhalation line, no instruction.
 *
 * The instruction is the package hazard-label placement (49 CFR 172.406(c))
 * or the bulk/portable-tank ID-number placement. The two are mutually
 * exclusive, so at most one applies.
 */
export function resolveDotBlockMarking(
  content: DotBlockMarkingContent,
  container: DotMarkingContainer | undefined,
  showDotBlock = true
): DotBlockMarking {
  const markingGroup = container ? dotMarkingGroupFor(container) : undefined;
  const showHazardLabels =
    showDotBlock && markingGroup !== undefined && requiresHazardLabel(markingGroup);
  // 49 CFR 172.313(a) applies to bulk packagings too, so this does not depend
  // on showHazardLabels.
  const inhalationLine =
    markingGroup === undefined ? undefined : poisonInhalationLine(content.dotHazardClass);

  // Counted from the labels that print at full size: class 7 draws the
  // trefoil on the label page but takes no regulated label.
  const regulated = [content.dotHazardClass, content.dotSubsidiaryHazardClass].filter(
    printsRegulatedHazardLabel
  ).length;
  if (showHazardLabels && regulated > 0) {
    return {
      showHazardLabels,
      inhalationLine,
      markingInstruction: packageLabelInstruction(regulated === 2),
    };
  }
  const bulk = markingGroup !== undefined && requiresLargeIdNumber(markingGroup);
  if (bulk && content.dotIdNumber) {
    return {
      showHazardLabels,
      inhalationLine,
      markingInstruction: bulkMarkingInstruction(
        markingGroup,
        container,
        inhalationLine !== undefined
      ),
    };
  }
  // No ID-number page, but the inhalation marking still goes on two sides.
  const inhalationOnly =
    bulk && inhalationLine
      ? inhalationMarkingInstruction(inhalationMarkingHeightMm(markingGroup, container))
      : undefined;
  return { showHazardLabels, inhalationLine, markingInstruction: inhalationOnly };
}
